import React, { useEffect, useRef } from 'react'
import { createUseStyles } from 'react-jss'
import get from 'lodash/get'
import cn from 'classnames'
import ProductDetail from '../ProductDetail'
import RecipeSliderSlice from './RecipeSliderSlice'
import StorySliderSlice from './StorySliderSlice'
import RelatedProductsSlice from './RelatedProductsSlice'
import YotpoReviewsSlice from './YotpoReviewsSlice'
import theme from '../../style/theme'

// Helper function to extract YouTube video ID from URL
const extractYouTubeId = (url) => {
  if (!url) return null

  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/
  const match = url.match(regExp)

  return (match && match[2].length === 11) ? match[2] : null
}
const extractInstagramId = (url) => {
  if (!url) return null

  // Match Instagram URLs like:
  // https://www.instagram.com/p/BHiV6LJhPo0/
  // https://www.instagram.com/p/BHiV6LJhPo0/?igsh=a2M3NmFuNDFyZXFs
  // https://instagram.com/p/BHiV6LJhPo0/
  const regExp = /(?:https?:\/\/)?(?:www\.)?instagram\.com\/p\/([A-Za-z0-9_-]+)\/?/
  const match = url.match(regExp)

  return match ? match[1] : null
}

// Helper function to get image URL from Sanity image object or fallback to Shopify URL
const getImageUrl = (image) => {
  if (!image) return null

  // If it's already a URL string
  if (typeof image === 'string') return image

  // If it's a Sanity image with processed sizes
  if (image.sizes && image.sizes.length > 0) {
    return image.sizes[image.sizes.length - 1].url // Use largest size
  }

  // If it has a direct URL property
  if (image.url) return image.url

  // If it's a Sanity image asset reference, construct URL
  if (image.asset && image.asset._ref) {
    const ref = image.asset._ref
    const projectId = process.env.REACT_APP_SANITY_PROJECT_ID || 'bhil6ydp'
    const dataset = process.env.REACT_APP_SANITY_DATASET || 'staging'
    // Parse the reference to get the image details
    const parts = ref.replace('image-', '').split('-')
    if (parts.length >= 3) {
      const hash = parts[0]
      const dimensions = parts[1]
      const format = parts[2]
      return `https://cdn.sanity.io/images/${projectId}/${dataset}/${hash}-${dimensions}.${format}`
    }
  }

  return null
}

// Enhanced image handling for Sanity assets
// const getProductImages = (product) => {
//   const images = []
//   // First, try to use processed Sanity images
//   if (product.store?.galleryImages?.length > 0) {
//     product.store.galleryImages.forEach(img => {
//       const imageUrl = getImageUrl(img)
//       if (imageUrl) {
//         images.push({
//           src: imageUrl,
//           alt: img.alt || product.store?.title || 'Product image',
//           width: img.width || 800,
//           height: img.height || 800
//         })
//       }
//     })
//   }
//   // If no gallery images, try featured image
//   if (images.length === 0 && product.store?.featuredImage) {
//     const imageUrl = getImageUrl(product.store.featuredImage)
//     if (imageUrl) {
//       images.push({
//         src: imageUrl,
//         alt: product.store.featuredImage.alt || product.store?.title || 'Product image',
//         width: 800,
//         height: 800
//       })
//     }
//   }
//   // Fallback to preview image URL
//   if (images.length === 0 && product.store?.previewImageUrl) {
//     images.push({
//       src: product.store.previewImageUrl,
//       alt: product.store?.title || 'Product image',
//       width: 800,
//       height: 800
//     })
//   }
//   return images
// }

const ProductDetailSlice = ({ slice, className }) => {
  const classes = useStyles()
  const product = get(slice, 'product')
  const sections = get(slice, 'sections', [])
  // ********************************************page reload start to load yotpo************************************************** */
  const reloadTriggeredRef = useRef(false)

  // Auto-reload functionality for product detail pages
  useEffect(() => {
    // Check if we've already reloaded for this product
    const currentProductId = product?._id || product?.id || product?.store?.slug
    const lastReloadedProduct = window.sessionStorage.getItem('lastReloadedProduct')
    const reloadFlag = window.sessionStorage.getItem('productPageReloadTriggered')

    if (!reloadTriggeredRef.current &&
        product &&
        currentProductId &&
        !reloadFlag &&
        lastReloadedProduct !== currentProductId) {
      reloadTriggeredRef.current = true

      // Detect if we're on an iPhone/iOS device
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream

      // Check if we're in a page transition state by looking for the curtain element
      const checkAndReload = () => {
        const curtain = document.getElementById('curtain')
        if (curtain) {
          // Check if transition animation is happening (curtain is visible/animating)
          const curtainStyles = window.getComputedStyle(curtain)
          const curtainOpacity = parseFloat(curtainStyles.opacity)
          const curtainTransform = curtainStyles.transform

          // Set flags to prevent reload loop
          window.sessionStorage.setItem('productPageReloadTriggered', 'true')
          window.sessionStorage.setItem('lastReloadedProduct', currentProductId)
          // If curtain is visible or transitioning, wait for animation to show, then reload
          if (curtainOpacity > 0 || curtainTransform.includes('translateY')) {
            // console.log('🔄 ProductDetailSlice: Page transition detected, scheduling reload...')
            // iPhone needs more time to show the transition animation properly
            const reloadDelay = isIOS ? 1200 : 400 // Longer delay for iOS

            setTimeout(() => {
              // console.log('🔄 ProductDetailSlice: Executing page reload after transition...')
              window.location.reload()
            }, reloadDelay)
          } else {
            // No visible transition detected, but still trigger one for iPhone
            if (isIOS) {
              // console.log('🔄 ProductDetailSlice: No transition detected on iOS, waiting longer...')
              // On iOS, sometimes the transition hasn't started yet, so wait longer
              setTimeout(() => {
                window.location.reload()
              }, 600)
            } else {
              // On other devices, if no transition is happening, don't reload
              // console.log('🔄 ProductDetailSlice: No transition detected on non-iOS, skipping reload')
              // Clear the flags since we're not reloading
              window.sessionStorage.removeItem('productPageReloadTriggered')
            }
          }
        } else {
          // No curtain element found
          if (isIOS) {
            // On iOS, still attempt reload after a reasonable delay
            // console.log('🔄 ProductDetailSlice: No curtain found on iOS, reloading anyway...')
            setTimeout(() => {
              window.location.reload()
            }, 500)
          } else {
            // On other devices, clear flags and don't reload
            window.sessionStorage.removeItem('productPageReloadTriggered')
          }
        }
      }

      // iPhone/iOS needs more time for components to mount and transitions to initialize
      const checkDelay = isIOS ? 100 : 30
      setTimeout(checkAndReload, checkDelay)
    }

    // Cleanup: Clear reload flag after component is stable (no reload needed)
    const clearReloadFlag = setTimeout(() => {
      if (window.sessionStorage.getItem('productPageReloadTriggered') === 'true') {
        window.sessionStorage.removeItem('productPageReloadTriggered')
      }
    }, 1000) // Increased to 2 seconds to account for longer product page transitions

    return () => clearTimeout(clearReloadFlag)
  }, [product]) // Depend on product to ensure we have the data before attempting reload

  // ********************************************page reload end************************************************** */

  // Return early if there's no product
  if (!product) {
    // console.warn('ProductDetailSlice: No product found in slice')
    return null
  } // console.log('ProductDetailSlice: Raw sections received:', sections)
  // console.log('ProductDetailSlice: Full slice data:', slice)
  // Check if there are any recipe-related fields directly in the slice
  if (slice.recipeSlider) {
    // console.log('Found recipe slider directly in slice:', slice.recipeSlider)
  }
  if (slice.recipeRefs) {
    // console.log('Found recipe refs directly in slice:', slice.recipeRefs)
  }

  // Check if there are any story-related fields directly in the slice
  if (slice.storySlider) {
    // console.log('Found story slider directly in slice:', slice.storySlider)
  }
  if (slice.storyRefs) {
    // console.log('Found story refs directly in slice:', slice.storyRefs)
  }

  // Process sections into a map by type
  // Some sections might come directly as objects with _type or type, while others might be in a sections array
  const sectionsByType = sections?.reduce((acc, section) => {
    if (!section) {
      return acc
    } // First check for _type (Sanity standard)
    if (section._type) {
      acc[section._type] = section
    } else if (section.type) {
      // Map the type to the expected key in our component
      if (section.type === 'recipeSlider') {
        // Ensure we preserve any existing recipes if they exist
        if (acc.recipeSlider) {
          // If we already have recipes, merge them
          // console.log('Merging recipe sliders:', acc.recipeSlider, section)

          // Merge slides arrays if they exist
          if (acc.recipeSlider.slides && section.slides) {
            acc.recipeSlider.slides = [...acc.recipeSlider.slides, ...section.slides]
          } else if (section.slides) {
            acc.recipeSlider.slides = section.slides
          }
          // Prefer the new section's title if it exists
          if (section.title) {
            acc.recipeSlider.title = section.title
          }
        } else {
          acc.recipeSlider = section
        }
        // console.log('Found recipe slider with type:', section)
      } else if (section.type === 'storySlider') {
        // Handle story slider similar to recipe slider
        if (acc.storySlider) {
          // If we already have stories, merge them
          // console.log('Merging story sliders:', acc.storySlider, section)

          // Merge slides arrays if they exist
          if (acc.storySlider.slides && section.slides) {
            acc.storySlider.slides = [...acc.storySlider.slides, ...section.slides]
          } else if (section.slides) {
            acc.storySlider.slides = section.slides
          }
          // Prefer the new section's title if it exists
          if (section.title) {
            acc.storySlider.title = section.title
          }
        } else {
          acc.storySlider = section
        }
        // console.log('Found story slider with type:', section)
      } else {
        // Save it with the same name as type
        acc[section.type] = section
      }
    } else if (section.recipeSlider) {
      // If it's a nested recipeSlider object, handle it
      if (acc.recipeSlider) {
        // If we already have recipes, merge the slides
        if (acc.recipeSlider.slides && section.recipeSlider.slides) {
          acc.recipeSlider.slides = [...acc.recipeSlider.slides, ...section.recipeSlider.slides]
        } else if (section.recipeSlider.slides) {
          acc.recipeSlider.slides = section.recipeSlider.slides
        }
      } else {
        acc.recipeSlider = section.recipeSlider
      }
    } else if (section.storySlider) {
      // If it's a nested storySlider object, handle it
      if (acc.storySlider) {
        // If we already have stories, merge the slides
        if (acc.storySlider.slides && section.storySlider.slides) {
          acc.storySlider.slides = [...acc.storySlider.slides, ...section.storySlider.slides]
        } else if (section.storySlider.slides) {
          acc.storySlider.slides = section.storySlider.slides
        }
      } else {
        acc.storySlider = section.storySlider
      }
    }

    return acc
  }, {})
  // console.log('ProductDetailSlice: Processed sections:', sectionsByType)

  const {
    productDescription: description,
    ingredientsSection: ingredients,
    nutritionalInfo,
    videoSection,
    recipeSlider,
    storySlider,
    relatedProducts,
    yotpoReviews
  } = sectionsByType || {}

  // If no recipe slider found in sections, check if it's directly in the slice
  let finalRecipeSlider = recipeSlider
  if (!finalRecipeSlider && slice.recipeSlider) {
    finalRecipeSlider = slice.recipeSlider
    // console.log('Using recipe slider from slice directly:', finalRecipeSlider)
  }
  if (!finalRecipeSlider && slice.recipeRefs) {
    finalRecipeSlider = {
      _type: 'recipeSlider',
      title: 'Related Recipes',
      recipeRefs: slice.recipeRefs
    }
    // console.log('Created recipe slider from recipe refs:', finalRecipeSlider)
  }

  // Handle story slider the same way as recipe slider
  let finalStorySlider = storySlider
  if (!finalStorySlider && slice.storySlider) {
    finalStorySlider = slice.storySlider
    // console.log('Using story slider from slice directly:', finalStorySlider)
  }
  if (!finalStorySlider && slice.storyRefs) {
    finalStorySlider = {
      _type: 'storySlider',
      title: 'Stories',
      storyRefs: slice.storyRefs
    }
    // console.log('Created story slider from story refs:', finalStorySlider)
  }

  // Handle related products - can come from product's relatedProducts field or slice
  let finalRelatedProducts = relatedProducts
  if (!finalRelatedProducts && product.store?.relatedProducts) {
    finalRelatedProducts = {
      _type: 'relatedProducts',
      title: product.store.relatedProducts.title || 'You might also like',
      products: product.store.relatedProducts.products || [],
      currentProductId: product._id || product.id,
      limit: 3
    }
    // console.log('Using related products from product store:', finalRelatedProducts)
  }
  if (!finalRelatedProducts && slice.relatedProducts) {
    finalRelatedProducts = {
      ...slice.relatedProducts,
      currentProductId: product._id || product.id
    }
    // console.log('Using related products from slice:', finalRelatedProducts)
  }

  // console.log('Final recipe slider to pass:', finalRecipeSlider)
  // console.log('Final story slider to pass:', finalStorySlider)
  // console.log('Final related products to pass:', finalRelatedProducts)
  // console.log('Video section data:', videoSection)

  // const images = getProductImages(product)

  return (
    <div className={cn(classes.container, className)}>
      <div className={classes.mainContent}>
        <ProductDetail
          product={product}
          isDetailed
          ingredients={ingredients} // Pass ingredients data
          nutritionalInfo={nutritionalInfo} // Pass nutritional info
          description={description} // Pass description
          relatedProducts={finalRelatedProducts} // Pass related products to control rendering
        />

        {finalRelatedProducts && (
          <div className={classes.relatedProductsSection}>
            <RelatedProductsSlice
              slice={finalRelatedProducts}
              className={classes.relatedProducts}
              key={`related-products-${product._id || product.id}-${finalRelatedProducts.products?.length || 0}`}
            />
          </div>
        )}

        {/* YotpoReviews section - manageable through Sanity */}
        {yotpoReviews && (
          <div className={classes.reviewsSection}>
            <YotpoReviewsSlice
              slice={yotpoReviews}
              product={product}
              className={classes.yotpoReviews}
              key={`yotpo-reviews-${product._id || product.id}`}
            />
          </div>
        )}

        {/* Handle sliders consistently in ProductDetailSlice */}
        {finalStorySlider && (
          <div className={classes.sliderSection} id='product-stories-section'>
            <StorySliderSlice
              slice={finalStorySlider}
              className={classes.productSlider}
              key={`story-${product._id || product.id}-${finalStorySlider.storyRefs?.length || finalStorySlider.slides?.length || 0}`}
            />
          </div>
        )}

        {finalRecipeSlider && (
          <div className={classes.sliderSection} id='product-recipes-section'>
            <RecipeSliderSlice
              slice={finalRecipeSlider}
              className={classes.productSlider}
              key={`recipe-${product._id || product.id}-${finalRecipeSlider.recipeRefs?.length || finalRecipeSlider.slides?.length || 0}`}
            />
          </div>
        )}
        {videoSection && (
          <div className={classes.videoSection}>
            {videoSection.backgroundImage && (
              <div
                className={classes.videoBackground}
                style={{
                  backgroundImage: `url(${getImageUrl(videoSection.backgroundImage)})`
                }}
              />
            )}
            {videoSection.quote && (
              <div className={classes.textContent}>
                {videoSection.quote.text}
              </div>
            )}
            <div className={classes.videoContent}>
              {videoSection.video && (
                <div className={classes.videoContainer}>
                  {videoSection.video.youtubeUrl ? (
                    <div className={classes.youtubeEmbed}>
                      <iframe
                        src={`https://www.youtube.com/embed/${extractYouTubeId(videoSection.video.youtubeUrl)}`}
                        title={videoSection.video.title || 'Video'}
                        frameBorder='0'
                        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                        allowFullScreen
                      />
                    </div>
                  ) : videoSection.video.instagramUrl ? (
                    <div className={classes.instagramEmbed}>
                      <iframe
                        src={`https://www.instagram.com/p/${extractInstagramId(videoSection.video.instagramUrl)}/embed/`}
                        title={videoSection.video.title || 'Instagram Video'}
                        frameBorder='0'
                        scrolling='no'
                        allowTransparency='true'
                        allowFullScreen
                      />
                    </div>
                  ) : videoSection.video.videoFile ? (
                    <video
                      className={classes.videoPlayer}
                      controls
                      poster={videoSection.video.poster?.url}
                    >
                      <source src={videoSection.video.videoFile.url} type='video/mp4' />
                      Your browser does not support the video tag.
                    </video>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const useStyles = createUseStyles({
  '@global': {
    // Hide Klaviyo popup on product detail pages
    '[role="dialog"][aria-label="POPUP Form"]': {
      display: 'none !important'
    },
    // Also hide any other Klaviyo modal dialogs on product pages
    '.needsclick.kl-private-reset-css-Xuajs1[role="dialog"]': {
      display: 'none !important'
    },
    // Hide Klaviyo forms with specific test IDs that might appear
    '[data-testid="klaviyo-form-XGPMhs"]': {
      display: 'none !important'
    }
  },
  container: {
    maxWidth: '100%',
    margin: '0 auto',
    paddingTop: '4rem'
  },
  mainContent: {
    marginBottom: '4rem'
  },
  section: {
    marginTop: '3rem',
    padding: '2rem 0',
    borderTop: `1px solid ${theme.colors.border}`
  },
  sectionTitle: {
    fontSize: '36px',
    marginBottom: '7.5rem',
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    color: theme.colors.primary
  },
  description: {
    fontSize: '1.125rem',
    lineHeight: 1.6,
    '& p': {
      marginBottom: '1rem'
    }
  },
  videoSection: {
    position: 'relative',
    padding: '2.5rem 4rem 15.6rem 4rem',
    backgroundColor: theme.colors.backgroundColors?.default?.background || '#f8f9fa',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      padding: '26px 22px',
      height: '100vh'
    }

  },
  videoBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    opacity: 1,
    zIndex: 1
  },
  videoContent: {
    position: 'relative',
    zIndex: 2,
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center'
  },
  textContent: {
    fontSize: '36px',
    color: '#D9E1CC',
    padding: '0',
    lineHeight: 1.4,
    fontFamily: theme.fonts.headings,
    position: 'relative',
    zIndex: 2,
    margin: '0 0 7.5rem 0',
    textAlign: 'left',
    [theme.breakpoints.down('md')]: {
      fontSize: '20px'
    }
  },
  videoContainer: {
    marginTop: '2rem'
  },
  youtubeEmbed: {
    position: 'relative',
    paddingBottom: '56.25%',
    height: 0,
    overflow: 'hidden',
    borderRadius: '8px',
    '& iframe': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none'
    }
  },
  instagramEmbed: {
    position: 'relative',
    width: '100%',
    maxWidth: '540px',
    margin: '0 auto',
    backgroundColor: '#fff',
    borderRadius: '8px',
    overflow: 'hidden',
    '& iframe': {
      width: '100%',
      height: '100%',
      minHeight: '500px',
      border: 'none',
      [theme.breakpoints.down('md')]: {
        minHeight: '400px'
      },
      [theme.breakpoints.down('sm')]: {
        minHeight: '390px'
      }
    }
  },
  videoPlayer: {
    width: '100%',
    maxWidth: '800px',
    height: 'auto',
    borderRadius: '8px'
  },
  sliderSection: {
    backgroundImage: 'url("/images/bg-image.jpg")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    width: '100%',
    padding: '85px 0',
    '& h2': {
      fontFamily: theme.fonts.headings,
      fontWeight: theme.fonts.headingsFontWeight,
      fontSize: '1.75rem',
      marginBottom: '1.5rem',
      color: theme.colors.primary
    },
    [theme.breakpoints.down('sm')]: {
      padding: '26px 0 !important',
      '&#product-recipes-section': {
        paddingBottom: '93px !important'
      }
    },
    '&#product-recipes-section': {
      padding: '85px 0 210px'
    }
  },
  productSlider: {
    // Custom styles specific to sliders within product detail
    marginBottom: '2rem',
    '& img': {
      objectFit: 'cover'
    },
    // Make sure the title is properly styled
    '& .titleContainerStatic': {
      marginBottom: '24px'
    },
    '& .titleStatic': {
      color: theme.colors.primary,
      fontFamily: theme.fonts.headings,
      fontWeight: theme.fonts.headingsFontWeight,
      fontSize: '28px'
    },
    // Improved styling for new tile layout
    '& .flickity-viewport': {
      overflow: 'visible'
    },
    // Improved navigation buttons
    '& .flickity-prev-next-button': {
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 1)'
      }
    }
  },
  relatedProductsSection: {
    margin: '150px auto 110px auto',
    padding: '0',
    width: '100%',
    maxWidth: 'calc(100% - 9rem)',

    [theme.breakpoints.down('sm')]: {
      margin: '110px auto 57px auto',
      maxWidth: '100%',
      padding: '0 1rem'
    }

  },
  relatedProducts: {
    // Override any conflicting styles from the parent
    margin: '0',
    padding: '0'
  },
  reviewsSection: {
    margin: '0',
    padding: '0'
  },
  yotpoReviews: {
    // Any custom styles for Yotpo reviews slice
    margin: '0',
    padding: '0'
  }
}, { name: 'ProductDetailSlice' })

export default ProductDetailSlice
